import { z } from 'zod';

/**
 * Schemas Zod del dominio Usuario. Alineados al contrato del backend
 * `ani-fiscalizacion-api` post Fase 9.5 (ADR-006 cerrado):
 *
 *  - `rolId: number` (coerced) — el backend recibe el id de Rol, no un enum.
 *  - `EstadoUsuario` PascalCase.
 *  - Filtros admin: `page`/`pageSize` para paginación page-based
 *    (`GET /admin/users` devuelve `{ items, meta }` con `total`/`totalPages`).
 *
 * Los selects de "rol asignable" / "perfil asignable" deben llenarse
 * con catálogos (`GET /admin/roles`, `GET /admin/perfiles`) filtrados
 * por `delegatable-permissions` (Fase 9).
 */

export const ESTADOS = ['Activo', 'Inactivo', 'Suspendido', 'PendienteVerificacion'] as const;
export const PAGE_SIZE_DEFAULT = 20;

const idNumerico = z
  .number({ message: 'Identificador inválido' })
  .int('Identificador inválido')
  .positive('Identificador inválido');

export const usuarioBaseSchema = z.object({
  nombre: z.string().min(2, 'Mínimo 2 caracteres').max(100, 'Máximo 100 caracteres'),
  email: z.string().min(1, 'Ingrese el correo').email('Correo inválido'),
  rolId: idNumerico,
  perfilId: idNumerico.nullable().optional(),
  nombreCompleto: z.string().max(200, 'Máximo 200 caracteres').optional(),
  telefono: z.string().max(40, 'Máximo 40 caracteres').optional(),
});

export const crearUsuarioSchema = usuarioBaseSchema;
export type CrearUsuarioInput = z.infer<typeof crearUsuarioSchema>;

/**
 * `PATCH /admin/users/:id` solo permite cambiar campos no sensibles
 * (`users.dto.ts:73-101`). El rol va por `PATCH .../role`, el perfil por
 * `PATCH .../perfil` y los permisos por `POST .../permissions(/revoke)`.
 * El estado solo admite `Activo | Suspendido`; para `Inactivo` usar el
 * endpoint dedicado de desactivación (Fase 15).
 */
export const actualizarUsuarioSchema = z.object({
  nombre: z.string().min(2, 'Mínimo 2 caracteres').max(100, 'Máximo 100 caracteres').optional(),
  nombreCompleto: z.string().max(200, 'Máximo 200 caracteres').optional(),
  telefono: z.string().max(40, 'Máximo 40 caracteres').optional(),
  estado: z.enum(['Activo', 'Suspendido']).optional(),
});
export type ActualizarUsuarioInput = z.infer<typeof actualizarUsuarioSchema>;

export const cambiarRolSchema = z.object({ rolId: idNumerico });
export type CambiarRolInput = z.infer<typeof cambiarRolSchema>;

export const asignarPerfilSchema = z.object({
  perfilId: idNumerico.nullable(),
});
export type AsignarPerfilInput = z.infer<typeof asignarPerfilSchema>;

export const grantPermisoSchema = z.object({
  permisoNombre: z
    .string()
    .min(1, 'Permiso requerido')
    .regex(/^([a-z_]+:[a-z_]+|\*)$/, 'Formato esperado <dominio>:<accion> o *'),
  expiresAt: z.string().datetime({ message: 'Fecha ISO inválida' }).optional(),
});
export type GrantPermisoInput = z.infer<typeof grantPermisoSchema>;

export const revokePermisoSchema = z.object({
  permisoNombre: z.string().min(1, 'Permiso requerido'),
});
export type RevokePermisoInput = z.infer<typeof revokePermisoSchema>;

export const filtrosUsuariosSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(PAGE_SIZE_DEFAULT),
  q: z.string().optional(),
  rolId: z.coerce.number().int().positive().optional(),
  perfilId: z.coerce.number().int().positive().optional(),
  estado: z.enum(ESTADOS).optional(),
});
export type FiltrosUsuariosInput = z.infer<typeof filtrosUsuariosSchema>;
